import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../utils/axios';
import { createAuthedThunk } from '../authedThunk';

const notesInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: [],
};

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
  data: null,
};

const initialState = {
  notes: { ...notesInitialState },
  createNote: { ...mutationInitialState },
  updateNote: { ...mutationInitialState },
  deleteNote: { ...mutationInitialState },
  // Per-entity counters keyed by entityId, e.g. { contact_1: { notes: 3 } }.
  // Only ever written from GET /us/entity-counts/{entityId} — the backend owns
  // these numbers, the frontend never increments them itself.
  entityCounts: {},
};

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

export const fetchNotesThunk = thunk(
  'notes/fetchNotes',
  async (token, { sourceId }) => {
    const r = await axios.get('/us/notes', {
      params: { 'request-id': uuidv4(), sourceId },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch notes');
  },
);

export const createNoteThunk = thunk(
  'notes/createNote',
  async (token, { sourceId, note }) => {
    const r = await axios.post(
      '/us/notes',
      { sourceId, note },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status >= 200 && r.status < 300) return r.data;
    throw new Error('Failed to create note');
  },
);

export const updateNoteThunk = thunk(
  'notes/updateNote',
  async (token, { noteId, note }) => {
    const r = await axios.put(
      `/us/notes/${noteId}`,
      { note },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status >= 200 && r.status < 300) return r.data;
    throw new Error('Failed to update note');
  },
);

export const deleteNoteThunk = thunk(
  'notes/deleteNote',
  async (token, { noteId }) => {
    const r = await axios.delete(`/us/notes/${noteId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status >= 200 && r.status < 300) return noteId;
    throw new Error('Failed to delete note');
  },
);

export const fetchEntityCountsThunk = thunk(
  'notes/fetchEntityCounts',
  async (token, { entityId }) => {
    const r = await axios.get(`/us/entity-counts/${entityId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return { entityId, counts: r.data?.counts || {} };
    throw new Error('Failed to fetch entity counts');
  },
);

/**
 * Notes count for an entity (contact/company/deal), preferring the fresher
 * value fetched into `state.notes.entityCounts` over the one embedded in the
 * entity's `customFields` by the list/detail endpoints.
 */
export const getEmbeddedNotesCount = (entity) => {
  const embedded = entity?.customFields?.entityCounts;
  const counts = embedded?.counts || embedded;
  const count = counts?.notes;
  return Number.isFinite(count) ? count : 0;
};

export const selectNotesCount = (entity) => (state) => {
  const fetched = state?.notes?.entityCounts?.[entity?.id]?.notes;
  if (Number.isFinite(fetched)) return fetched;
  return getEmbeddedNotesCount(entity);
};

const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    resetNotes: (state) => {
      state.notes = { ...notesInitialState };
      state.createNote = { ...mutationInitialState };
      state.updateNote = { ...mutationInitialState };
      state.deleteNote = { ...mutationInitialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotesThunk.pending, (s) => {
        s.notes.fetching = true;
        s.notes.fetchingSuccessful = false;
        s.notes.fetchingFailed = false;
      })
      .addCase(fetchNotesThunk.fulfilled, (s, a) => {
        s.notes.fetching = false;
        s.notes.fetchingSuccessful = true;
        s.notes.fetchingFailed = false;
        s.notes.data = Array.isArray(a.payload) ? a.payload : [];
      })
      .addCase(fetchNotesThunk.rejected, (s, a) => {
        s.notes.fetching = false;
        s.notes.fetchingSuccessful = false;
        s.notes.fetchingFailed = true;
        s.notes.error = a.payload;
      });

    builder
      .addCase(createNoteThunk.pending, (s) => {
        s.createNote.processing = true;
        s.createNote.done = false;
        s.createNote.failed = false;
      })
      .addCase(createNoteThunk.fulfilled, (s, a) => {
        s.createNote.processing = false;
        s.createNote.done = true;
        s.createNote.failed = false;
        s.createNote.data = a.payload;
        // The list endpoint sorts by createdOn descending, so a new note goes
        // on top rather than at the end.
        s.notes.data = [a.payload, ...s.notes.data];
      })
      .addCase(createNoteThunk.rejected, (s, a) => {
        s.createNote.processing = false;
        s.createNote.done = false;
        s.createNote.failed = true;
        s.createNote.error = a.payload;
      });

    builder
      .addCase(updateNoteThunk.pending, (s) => {
        s.updateNote.processing = true;
        s.updateNote.done = false;
        s.updateNote.failed = false;
      })
      .addCase(updateNoteThunk.fulfilled, (s, a) => {
        s.updateNote.processing = false;
        s.updateNote.done = true;
        s.updateNote.failed = false;
        s.updateNote.data = a.payload;
        s.notes.data = s.notes.data.map((n) =>
          n?.id === a.payload?.id ? a.payload : n,
        );
      })
      .addCase(updateNoteThunk.rejected, (s, a) => {
        s.updateNote.processing = false;
        s.updateNote.done = false;
        s.updateNote.failed = true;
        s.updateNote.error = a.payload;
      });

    builder
      .addCase(deleteNoteThunk.pending, (s) => {
        s.deleteNote.processing = true;
        s.deleteNote.done = false;
        s.deleteNote.failed = false;
      })
      .addCase(deleteNoteThunk.fulfilled, (s, a) => {
        s.deleteNote.processing = false;
        s.deleteNote.done = true;
        s.deleteNote.failed = false;
        s.deleteNote.data = a.payload;
        s.notes.data = s.notes.data.filter((n) => n?.id !== a.payload);
      })
      .addCase(deleteNoteThunk.rejected, (s, a) => {
        s.deleteNote.processing = false;
        s.deleteNote.done = false;
        s.deleteNote.failed = true;
        s.deleteNote.error = a.payload;
      });

    builder.addCase(fetchEntityCountsThunk.fulfilled, (s, a) => {
      if (!a.payload?.entityId) return;
      s.entityCounts[a.payload.entityId] = a.payload.counts;
    });
  },
});

export const { resetNotes } = notesSlice.actions;
export default notesSlice.reducer;
